import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { QUOTE_TYPES, inr } from "@/lib/dseQuotes";
import DseQuoteActions from "@/components/dse/DseQuoteActions";

export default function DseMyQuotes({ scopeLabel }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Promise.all(Object.entries(QUOTE_TYPES).map(([type, cfg]) =>
        api.get(cfg.base, { params: { q: q || undefined, limit: 50 } })
          .then(({ data }) => (data.rows || []).map((r) => ({ ...r, _type: type })))));
      setRows(res.flat().sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const count = useMemo(() => rows.length, [rows]);

  return (
    <div className="space-y-3" data-testid="dse-my-quotes">
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, phone, model…"
          data-testid="dse-quotes-search"
          className="w-full border border-canvas-line bg-white pl-9 pr-3 py-3 text-sm focus:outline-none focus:border-ink" />
      </div>
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink-muted" data-testid="dse-quotes-count">
        {loading ? "Loading…" : `${count} quotes · ${scopeLabel}`}
      </div>
      {!loading && count === 0 && <div className="p-6 text-center text-sm text-ink-muted border border-canvas-line bg-white">No quotes yet.</div>}
      {rows.map((r) => {
        const cfg = QUOTE_TYPES[r._type];
        const emi = cfg.emi(r);
        return (
          <div key={r.id} className="bg-white border border-canvas-line p-4 space-y-3" data-testid={`dse-quote-${r.serial}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-[10px] tracking-widest text-ink-muted">{r.serial} · {cfg.label.toUpperCase()} · {r.quote_date}</div>
                <div className="font-semibold text-ink truncate">{r.customer?.name || "—"}</div>
                <div className="text-xs text-ink-muted truncate">{r.vehicle_model}</div>
                {r.created_by_name && <div className="text-[11px] text-ink-subtle">by {r.created_by_name}</div>}
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono font-semibold">{inr(cfg.total(r))}</div>
                {emi?.emi ? <div className="font-mono text-xs text-emerald-700">EMI {inr(emi.emi)} × {emi.tenure}</div> : null}
                <div className="mt-1 inline-block px-2 py-0.5 text-[10px] font-mono border border-canvas-line">{r.status}</div>
              </div>
            </div>
            <DseQuoteActions type={r._type} quote={r} testPrefix={`dse-quote-${r.serial}`} />
          </div>
        );
      })}
    </div>
  );
}
